import { Play as PlayIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';

type Props = {
  url: string;
};

const MAX_RETRIES = 3;

const ResponsivePlayer: React.FC<Props> = ({ url }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const userAgent = navigator.userAgent;
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Capacitor injects its native bridge into the WebView regardless of the
  // served origin, so this is the reliable "are we in the app?" signal. The
  // iOS app loads from https://schedule.healthcom.io (iosScheme: 'https' in
  // capacitor.config.ts), so the legacy `capacitor://` URL check below never
  // matches — it (and the others) are kept only as best-effort fallbacks for
  // non-Capacitor standalone/in-app-browser contexts.
  const isCapacitorNative =
    (
      window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor?.isNativePlatform?.() ?? false;

  const isIOSApp = useMemo(() => {
    if (!isIOS) return false;
    if (isCapacitorNative) return true;
    if (window.location.href.includes('capacitor://')) return true;
    if ((window.navigator as any).standalone) return true;
    if (userAgent.includes('Mobile/') && !userAgent.includes('Safari/')) return true;
    return false;
  }, [isIOS, isCapacitorNative, userAgent]);

  const shouldForceHLS = useMemo(() => {
    if (!url?.includes('.m3u8')) return false;
    return !isSafari;
  }, [url, isSafari]);

  // Treat ANY direct, natively-playable media file (mp4, HLS, mov, webm, …) as
  // "native video" on iOS — exercise videos are usually .mp4, not just .m3u8.
  //
  // Keyed off `isIOS`, NOT `isIOSApp`: the app loads from the remote https
  // origin, so every "is this the Capacitor app?" signal is unreliable, and when
  // that detection returns false the video falls into the inline ReactPlayer
  // <video controls> below — whose native control hit-region is broken inside the
  // app's custom `#root` overflow scroller (dead buttons in portrait). `isIOS` is
  // reliable (an iPhone/iPad UA always contains the device name), so any iOS
  // device viewing a direct video file gets the fullscreen-player path that
  // sidesteps the inline-controls bug. YouTube/Vimeo (no media extension) stay on
  // ReactPlayer, which embeds them via an <iframe> the bug doesn't affect.
  const isDirectVideoFile = useMemo(() => {
    if (!url) return false;
    let path = url;
    try {
      path = new URL(url, window.location.href).pathname;
    } catch {
      // Relative/malformed URL — fall back to matching the raw string.
    }
    return /\.(m3u8|mp4|m4v|mov|webm|ogg|ogv)$/i.test(path);
  }, [url]);

  const sourceType = useMemo(() => {
    const u = url?.toLowerCase() ?? '';
    if (u.includes('.m3u8')) return 'application/x-mpegURL';
    if (u.includes('.mp4') || u.includes('.m4v')) return 'video/mp4';
    if (u.includes('.mov')) return 'video/quicktime';
    if (u.includes('.webm')) return 'video/webm';
    if (u.includes('.ogv') || u.includes('.ogg')) return 'video/ogg';
    return undefined;
  }, [url]);

  const useNativeVideo = isIOS && isDirectVideoFile;

  // Cache-bust the source on every retry to bypass a stale CloudFront edge
  // response that errored the previous attempt. Gating purely on `retryCount`
  // (not a separate `error` flag) means the very first retry carries a fresh `_t`.
  const videoUrl = useMemo(() => {
    if (retryCount === 0) return url;
    return `${url}${url.includes('?') ? '&' : '?'}_t=${Date.now()}`;
  }, [url, retryCount]);

  // Schedule a single retry. The ref guard collapses simultaneous error events
  // into one pending retry, and the functional updater caps the count, so a
  // burst of errors can't stack timers or overshoot MAX_RETRIES.
  const scheduleRetry = useCallback(() => {
    if (retryTimer.current) return;
    retryTimer.current = setTimeout(() => {
      retryTimer.current = null;
      setRetryCount(prev => (prev < MAX_RETRIES ? prev + 1 : prev));
    }, 1000);
  }, []);

  // A successful load cancels any retry queued from a transient earlier error
  // so it can't needlessly reload a now-working video. We intentionally do not
  // reset `retryCount` here — the cache-busted URL is the one that worked, and
  // reverting it would remount the video and risk re-hitting the stale cache.
  const cancelPendingRetry = useCallback(() => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
  }, []);

  useEffect(() => () => cancelPendingRetry(), [cancelPendingRetry]);

  // Open iOS's native fullscreen player. Because the inline element is
  // `playsInline`, play() alone would play inline — so webkitEnterFullscreen()
  // (available on every iOS video element) is what forces fullscreen. Both calls
  // run synchronously inside the tap so iOS honors the user-gesture requirement.
  const handlePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const enterFullscreen = (video as unknown as { webkitEnterFullscreen?: () => void })
      .webkitEnterFullscreen;
    if (enterFullscreen) enterFullscreen.call(video);
    video.play().catch(() => {});
  }, []);

  // Native <video> error/load handling. The media `error` event fires on the
  // element (not the <source>) and doesn't bubble to React's synthetic
  // onError reliably, so attach it directly via the ref.
  useEffect(() => {
    if (!useNativeVideo) return;
    const video = videoRef.current;
    if (!video) return;

    video.addEventListener('error', scheduleRetry);
    video.addEventListener('loadeddata', cancelPendingRetry);
    return () => {
      video.removeEventListener('error', scheduleRetry);
      video.removeEventListener('loadeddata', cancelPendingRetry);
    };
  }, [useNativeVideo, videoUrl, scheduleRetry, cancelPendingRetry]);

  if (!url) return null;

  if (useNativeVideo) {
    // iOS/WKWebView miscomputes the touch hit-region of a native <video>'s
    // inline controls inside this app's custom `#root` overflow scroller — the
    // buttons render but their targets are offset, so they're dead in portrait.
    //
    // Sidestep it entirely by playing in iOS's native fullscreen player (immune
    // to the bug; gives the full control set — pause, scrub, ±10s, mute, PiP):
    //   • The inline element keeps `playsInline` and has NO `controls`, so iOS
    //     renders a clean frame with no overlaid controls or default play glyph
    //     (a non-`playsInline` video shows iOS's own fullscreen-play overlay,
    //     which is the stray "buttons over the top" when not in fullscreen).
    //   • A real DOM <button> (ordinary hit-testing, unaffected by the bug)
    //     triggers playback and calls webkitEnterFullscreen() to force the
    //     fullscreen player regardless of `playsInline`.
    return (
      <div
        className="relative w-full overflow-hidden rounded-lg bg-black"
        style={{ aspectRatio: '16 / 9' }}
      >
        <video
          ref={videoRef}
          key={videoUrl}
          preload="metadata"
          playsInline
          crossOrigin="anonymous"
          className="h-full w-full"
          style={{ objectFit: 'contain' }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        >
          <source src={videoUrl} type={sourceType} />
          Your browser does not support the video tag.
        </video>
        {!isPlaying && (
          <button
            type="button"
            aria-label="Play video"
            onClick={handlePlay}
            className="absolute inset-0 flex items-center justify-center"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/60">
              <PlayIcon className="ml-1 h-8 w-8 text-white" />
            </span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="player-wrapper rounded-lg overflow-hidden">
      <ReactPlayer
        className="react-player"
        url={videoUrl}
        width="100%"
        height="100%"
        controls
        pip={!isIOSApp}
        config={{
          file: {
            attributes: {
              controlsList: 'nodownload',
              playsInline: true,
              crossOrigin: 'anonymous',
            },
            forceVideo: true,
            forceHLS: shouldForceHLS,
            hlsOptions: {
              enableWorker: false,
              lowLatencyMode: false,
              backBufferLength: 30,
              maxBufferLength: 60,
              maxMaxBufferLength: 120,
              xhrSetup: (xhr: XMLHttpRequest) => {
                xhr.withCredentials = false;
                xhr.setRequestHeader('Cache-Control', 'no-cache');
              },
            },
          },
        }}
        playing={false}
        playsinline
        onError={scheduleRetry}
        onReady={cancelPendingRetry}
      />
    </div>
  );
};

export default ResponsivePlayer;
